/* eslint-disable @typescript-eslint/no-explicit-any */

import IconAngleBottom from '@assets/icons/angle-bottom.svg?react';
import IconAngleTop from '@assets/icons/angle-top.svg?react';
import IconArrow from '@assets/icons/big arrow left.svg?react';
import IconCalendar from '@assets/icons/calendar.svg?react';
import IconClipboard from '@assets/icons/clipboard.svg?react';
import IconClose from '@assets/icons/close.svg?react';
import IconDashboard from '@assets/icons/dashboard.svg?react';
import IconData from '@assets/icons/data.svg?react';
import IconEmail from '@assets/icons/email.svg?react';
import IconInfo from '@assets/icons/info.svg?react';
import IconLock from '@assets/icons/lock.svg?react';
import IconMeter from '@assets/icons/meter.svg?react';
import IconShow from '@assets/icons/show.svg?react';
import IconSign from '@assets/icons/sign.svg?react';
import IconTables from '@assets/icons/tables.svg?react';
import IconUser from '@assets/icons/user.svg?react';
import IconWarehouse from '@assets/icons/warehouse.svg?react';
import LogoDark from '@assets/logo-dark.svg?react';
import LogoSmall from '@assets/logo-small.svg?react';
import Logo from '@assets/logo.svg?react';
import {
	Box,
	Button,
	Chip,
	Collapse,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	type Theme,
	Tooltip,
	useColorScheme,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import {
	type Dispatch,
	type SetStateAction,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import buildversion from '../../../../buildversion?raw';

import { useAuthContext } from '@/contexts/AuthContext';
import layoutSizes from '../../../utils/layoutSizes';
import themeOptions from '../../../utils/theme';
import { CONFIGURATOR_ROLE_KEY } from '../../admin/widgetsGrids/types';
import type { DynamicTable } from '../../dynamicTables/dynamicTables.types';
import { listTables } from '../../dynamicTables/dynamicTablesApi';
import LazySvg from '../LazySvg';

type MenuSidebarProps = {
	isSidebarOpen: boolean;
	setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
	isAdmin?: boolean;
};

// Categories are collapsed by default; the user's manual expand/collapse
// choices are remembered per category key across sessions.
const OPEN_CATEGORIES_STORAGE_KEY = 'menu.openCategories';

const readStoredOpenCategories = (): Record<string, boolean> => {
	try {
		return JSON.parse(
			localStorage.getItem(OPEN_CATEGORIES_STORAGE_KEY) ?? '{}',
		);
	} catch {
		return {};
	}
};

const MenuSidebar = ({
	isSidebarOpen,
	setIsSidebarOpen,
	isAdmin = false,
}: MenuSidebarProps) => {
	const location = useLocation();
	const appVersion = buildversion.trim();

	const { mode, systemMode } = useColorScheme();
	const resolvedMode = mode === 'system' ? systemMode : mode;

	const isLargerThanMd = useMediaQuery(themeOptions().breakpoints.up('md'));

	const colorTheme = useTheme();
	const { palette } = colorTheme;

	const { t } = useTranslation();
	const { hasRole } = useAuthContext();

	// Runtime-created tables get their own client-menu category, one item per
	// table. Silent failure (403 / not provisioned / network) just hides the
	// category — the sidebar must never error over an optional feature.
	const [dynamicTables, setDynamicTables] = useState<DynamicTable[]>([]);
	useEffect(() => {
		if (isAdmin) return;
		let cancelled = false;
		listTables()
			.then((tables) => {
				if (!cancelled) setDynamicTables(tables);
			})
			.catch(() => {
				if (!cancelled) setDynamicTables([]);
			});
		return () => {
			cancelled = true;
		};
	}, [isAdmin]);

	const filterItemsByRoles = (items: any[]) =>
		items.filter((item: any) => {
			if (!item?.role || typeof item.role !== 'string') {
				return true;
			}

			const [resourceName, actionName] = item.role.split('.');

			if (!resourceName || !actionName) {
				return true;
			}

			return hasRole(resourceName, actionName);
		});

	const filterMenuByRoles = (menuCategories: any[]) =>
		menuCategories
			.map((menuCategory) => {
				const filteredItems = filterItemsByRoles(menuCategory.items || []);

				return {
					...menuCategory,
					items: filteredItems,
				};
			})
			.filter(
				(menuCategory) =>
					Array.isArray(menuCategory.items) && menuCategory.items.length > 0,
			);

	const menuItemsData = useMemo(() => {
		if (isAdmin) {
			return filterMenuByRoles([
				// {
				//  title: t('menu.dashboard'),
				//  Icon: IconDashboard,
				//  open: false,
				// items: [
				//  {
				//   url: '/admin/dashboard',
				//   title: t('menu.dashboard'),
				//   role: 'Results.GetAll',
				//  },
				// ],
				// },
				// {
				//  title: t('menu.settings'),
				//  Icon: IconSettings,
				//  open: false,
				// items: [
				//  {
				//   url: '/admin/licenses',
				//   title: t('menu.licenses'),
				//   role: 'Licenses.GetAll',
				//  },
				// ],
				// },
				{
					key: 'users',
					title: t('menu.users'),
					Icon: IconUser,
					open: false,
					items: [
						{
							url: '/admin/users',
							title: t('menu.users'),
							role: 'Users.GetAll',
						},
						{
							url: '/admin/accessGroups',
							title: t('menu.accessGroups'),
							role: 'AccessGroups.GetAll',
						},
					],
				},
				{
					key: 'devices',
					title: t('menu.devices'),
					Icon: IconMeter,
					open: false,
					items: [
						{
							url: '/admin/devices',
							title: t('menu.devices'),
						},
					],
				},
				{
					key: 'audit',
					title: t('menu.audit'),
					Icon: IconInfo,
					open: false,
					items: [
						{
							url: '/admin/audit',
							title: t('menu.audit'),
							role: 'Audit.GetAll',
						},
					],
				},
				{
					key: 'taskScheduler',
					title: t('entities.taskScheduler'),
					Icon: IconCalendar,
					open: false,
					items: [
						{
							url: '/admin/taskScheduler',
							title: t('entities.taskScheduler'),
							role: 'TaskScheduler.GetAll',
						},
					],
				},
				{
					key: 'widgetsGrids',
					title: t('menu.widgetsGrids'),
					Icon: IconDashboard,
					open: false,
					items: [
						{
							url: '/admin/widgets-grids',
							title: t('menu.widgetsGrids'),
							role: CONFIGURATOR_ROLE_KEY,
						},
					],
				},
				{
					key: 'reports',
					title: t('entities.reports'),
					Icon: IconData,
					open: false,
					items: [
						{
							url: '/admin/reports',
							title: t('reports.manager'),
						},
					],
				},
				{
					key: 'adminDynamicTables',
					title: t('menu.dynamicTables'),
					Icon: IconTables,
					open: false,
					items: [
						{
							// Table management list calls GET /tables (GetTables role);
							// GetAll is the row-read role and does not grant this page.
							url: '/admin/dynamic-tables',
							title: t('dynamicTables.title'),
							role: 'DynamicTables.GetTables',
						},
						{
							url: '/admin/dynamic-tables/new',
							title: t('dynamicTables.newTable'),
							role: 'DynamicTables.CreateTable',
						},
					],
				},
				// {
				//  title: t('menu.history'),
				//  Icon: IconInfo,
				//  open: false,
				// items: [
				//  {
				//   url: '/admin/history',
				//   title: t('menu.history'),
				//   role: 'History.GetAll',
				//  },
				// ],
				// },
			]);
		}

		return filterMenuByRoles([
			{
				key: 'dashboard',
				title: t('entities.dashboard'),
				Icon: IconDashboard,
				open: false,
				direct: true,
				items: [
					{
						url: '/dashboard',
						title: t('entities.dashboard'),
						role: 'Results.GetAll',
					},
				],
			},
			{
				key: 'data',
				title: t('entities.data'),
				Icon: IconData,
				open: false,
				items: [
					{
						url: '/results',
						title: t('entities.results'),
						role: 'Results.GetAll',
					},
					{
						url: '/customers',
						title: t('entities.customers'),
						role: 'Customers.GetAll',
					},
					{
						url: '/drivers',
						title: t('entities.drivers'),
						role: 'Drivers.GetAll',
					},
					{
						url: '/vehicles',
						title: t('entities.vehicles'),
						role: 'Vehicles.GetAll',
					},
					{
						url: '/products',
						title: t('entities.products'),
						role: 'Products.GetAll',
					},
					{
						url: '/destPoint',
						title: t('entities.destPoint'),
						role: 'DestPoint.GetAll',
					},
					{
						url: '/cards',
						title: t('entities.cards'),
						role: 'Cards.GetAll',
					},
				],
			},
			{
				key: 'orders',
				title: t('entities.orders'),
				Icon: IconClipboard,
				open: false,
				direct: true,
				items: [
					{
						url: '/orders',
						title: t('entities.orders'),
						role: 'Orders.GetAll',
					},
				],
			},
			{
				key: 'contracts',
				title: t('entities.contracts'),
				Icon: IconSign,
				open: false,
				direct: true,
				items: [
					{
						url: '/contracts',
						title: t('entities.contracts'),
						role: 'Contracts.GetAll',
					},
				],
			},
			{
				key: 'warehouse',
				title: t('entities.warehouse'),
				Icon: IconWarehouse,
				open: false,
				direct: true,
				items: [
					{
						url: '/warehouse',
						title: t('entities.warehouse'),
						role: 'Warehouse.GetAll',
					},
				],
			},
			{
				key: 'spy',
				title: t('entities.spy'),
				Icon: IconShow,
				open: false,
				direct: true,
				items: [
					{
						url: '/spy',
						title: t('entities.spy'),
						role: 'Spy.GetAll',
					},
				],
			},
			{
				key: 'requestMessages',
				title: t('entities.requestedMsg'),
				Icon: IconEmail,
				open: false,
				direct: true,
				items: [
					{
						url: '/requestMessages',
						title: t('entities.requestedMsg'),
						role: 'RequestedMsg.GetAll',
					},
				],
			},
			{
				key: 'accessControl',
				title: t('entities.cards/cardtimes'),
				Icon: IconLock,
				open: false,
				direct: true,
				items: [
					{
						url: '/cards/cardtimes',
						title: t('entities.cards/cardtimes'),
						role: 'Cards.GetCardTimesAll',
					},
				],
			},
			{
				key: 'dynamicTables',
				title: t('menu.dynamicTables'),
				Icon: IconTables,
				open: false,
				items: dynamicTables.map((table) => ({
					url: `/dynamic-tables/${table.Name}`,
					title: table.Name,
					role: 'DynamicTables.GetAll',
				})),
			},
		]);
	}, [isAdmin, t, hasRole, dynamicTables]);

	const [menuItems, setMenuItems] = useState(() => {
		const stored = readStoredOpenCategories();

		return menuItemsData.map((menuItem: any) => ({
			...menuItem,
			open: stored[menuItem.key] ?? menuItem.open,
		}));
	});

	// Rebuild when data changes (language switch, dynamic tables loaded) but
	// keep whatever open state the user has set.
	useEffect(() => {
		setMenuItems((previousMenuItems) => {
			const stored = readStoredOpenCategories();

			return menuItemsData.map((menuItem: any) => {
				const previousItem = previousMenuItems.find(
					(item: any) => item.key === menuItem.key,
				);

				return {
					...menuItem,
					open: previousItem?.open ?? stored[menuItem.key] ?? menuItem.open,
				};
			});
		});
	}, [menuItemsData]);

	const handleMenuItemClick = (index: number) => {
		setMenuItems((previousMenuItems) => {
			const nextMenuItems = previousMenuItems.map(
				(menuItem: any, menuItemIndex: number) =>
					menuItemIndex === index
						? { ...menuItem, open: !menuItem.open }
						: menuItem,
			);

			const toggledItem: any = nextMenuItems[index];
			if (toggledItem?.key) {
				try {
					localStorage.setItem(
						OPEN_CATEGORIES_STORAGE_KEY,
						JSON.stringify({
							...readStoredOpenCategories(),
							[toggledItem.key]: toggledItem.open,
						}),
					);
				} catch {
					// Storage unavailable — the choice just won't survive a reload.
				}
			}

			return nextMenuItems;
		});
	};
	return (
		<Drawer
			sx={{
				flexShrink: 0,
				'& .MuiPaper-root': {
					boxShadow: 'none',
				},
				'& .MuiDrawer-paper': (theme: Theme) => ({
					border: 0,
					background:
						theme.palette.mode === 'dark' ? '#15181D' : theme.palette.kemek.bg1,
					//  boxSizing: 'border-box',
					width: isSidebarOpen
						? { xs: '100%', md: layoutSizes.sidebarWidth }
						: { xs: 0, md: 76 },
				}),
				'& .MuiListItemIcon-root ': {
					mr: 2,
					minWidth: 32,
					height: 32,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				},
			}}
			variant="permanent"
			anchor="left"
			elevation={0}
		>
			<Box
				sx={{
					height: '72px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					//  padding: '21px',
					borderBottom: (theme: Theme) =>
						`1px solid ${theme.palette.kemek.lightestGray}`,
				}}
			>
				<IconButton
					title={t('global.close')}
					aria-label={t('global.close')}
					onClick={() => setIsSidebarOpen(!isSidebarOpen)}
					sx={{
						display: { xs: 'block', md: 'none' },
						position: 'absolute',
						right: 16,
						top: 16,
					}}
				>
					<IconClose />
				</IconButton>
				{resolvedMode === 'light' && (
					<Link to="/results">
						{isSidebarOpen ? (
							<Logo style={{ display: 'block' }} />
						) : (
							<LogoSmall style={{ display: 'block' }} />
						)}
					</Link>
				)}
				{resolvedMode === 'dark' && (
					<Link to="/results">
						{isSidebarOpen ? (
							<LogoDark style={{ display: 'block' }} />
						) : (
							<LogoSmall style={{ display: 'block' }} />
						)}
					</Link>
				)}
			</Box>

			{isAdmin && isSidebarOpen && (
				<Box sx={{ px: 2, pt: 4 }}>
					<Chip
						label={t('menu.administrative')}
						color="default"
						sx={{
							background: 'transparent',
							border: '1px solid',
							fontWeight: 500,
							borderColor: (theme: Theme) => theme.palette.kemek.lightGray,
							color: (theme: Theme) => theme.palette.kemek.darkestGray,
						}}
						size="small"
						className="mt-1"
					/>
				</Box>
			)}

			<Box
				sx={{
					p: 2,
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
				}}
			>
				<List
					sx={{
						padding: 0,
						// marginBottom: 'auto',
						// flex: 1,
					}}
				>
					{!isSidebarOpen &&
						menuItems.map(({ title, Icon, open, items, direct }, index) => {
							const hasActiveItem = items?.some(
								(item: any) => item.url === location.pathname,
							);

							if (direct && items?.[0]?.url) {
								return (
									<Box key={title}>
										<Link to={items[0].url}>
											<Tooltip title={title} placement="right" arrow>
												<ListItemButton
													aria-label={title}
													sx={{
														padding: '12px',
														justifyContent: 'center',
														height: 44,
														width: 44,
														background: hasActiveItem
															? palette.kemek.blue
															: 'transparent',
														borderRadius: '4px',
													}}
												>
													<ListItemIcon sx={{ mr: '0 !important' }}>
														<Icon
															style={{
																color: hasActiveItem
																	? palette.kemek.white
																	: 'initial',
															}}
														/>
													</ListItemIcon>
												</ListItemButton>
											</Tooltip>
										</Link>
									</Box>
								);
							}

							return (
								<Box key={title}>
									<Tooltip title={title} placement="right" arrow>
										<ListItemButton
											aria-label={title}
											onClick={() => {
												setIsSidebarOpen(true);
												handleMenuItemClick(index);
											}}
											sx={{
												padding: '12px',
												justifyContent: 'center',
												height: 44,
												width: 44,
												background: hasActiveItem
													? palette.kemek.blue
													: 'transparent',
												borderRadius: '4px',
											}}
										>
											<ListItemIcon sx={{ mr: '0 !important' }}>
												<Icon
													style={{
														color: hasActiveItem
															? palette.kemek.white
															: 'initial',
													}}
												/>
											</ListItemIcon>
										</ListItemButton>
									</Tooltip>
								</Box>
							);
						})}

					{isSidebarOpen &&
						menuItems.map(({ title, Icon, open, items, direct }, index) => {
							if (direct && items?.[0]?.url) {
								const isActive = location.pathname === items[0].url;

								return (
									<Box key={title}>
										<Link
											style={{ width: '100%' }}
											to={items[0].url}
											onClick={() => {
												if (!isLargerThanMd) {
													setIsSidebarOpen(false);
												}
											}}
										>
											<ListItemButton
												sx={{
													pl: '12px',
													background: isActive
														? palette.kemek.blue
														: 'transparent',
													borderRadius: '4px',
													'&:hover': {
														background: `${
															isActive
																? palette.kemek.blue
																: palette.kemek.lightestGray
														} !important`,
													},
												}}
											>
												<ListItemIcon>
													<Icon
														style={{
															color: isActive
																? palette.kemek.white
																: palette.kemek.darkestGray,
														}}
													/>
												</ListItemIcon>

												<ListItemText
													primaryTypographyProps={{
														style: {
															color: isActive
																? 'white'
																: palette.kemek.darkestGray,
														},
													}}
													primary={title}
												/>
											</ListItemButton>
										</Link>
									</Box>
								);
							}

							return (
								<Box key={title}>
									<ListItemButton
										onClick={() => handleMenuItemClick(index)}
										sx={{ pl: '12px' }}
									>
										<ListItemIcon>
											<Icon style={{ color: palette.kemek.darkestGray }} />
										</ListItemIcon>

										<ListItemText primary={title} />

										{open ? (
											<IconAngleTop
												style={{ color: palette.kemek.darkestGray }}
											/>
										) : (
											<IconAngleBottom
												style={{ color: palette.kemek.darkestGray }}
											/>
										)}
									</ListItemButton>

									{items &&
										items.map((item: any) => (
											<Collapse
												in={open}
												timeout="auto"
												unmountOnExit
												key={`${item.title}`}
											>
												<List sx={{ padding: 0 }}>
													<ListItem component="div" disablePadding>
														{typeof item?.url !== 'undefined' ? (
															<Link
																style={{ width: '100%' }}
																to={item?.url}
																onClick={() => {
																	if (!isLargerThanMd) {
																		setIsSidebarOpen(false);
																	}
																}}
															>
																<ListItemButton
																	sx={{
																		pl: 2,
																		background:
																			location.pathname === item.url
																				? palette.kemek.blue
																				: 'transparent',
																		borderRadius: '4px',
																		maxHeight: 42,
																		'&:hover': {
																			background: `${
																				location.pathname === item.url
																					? palette.kemek.blue
																					: palette.kemek.lightestGray
																			} !important`,
																		},
																	}}
																>
																	<ListItemIcon>
																		{item.icon && <LazySvg name={item.icon} />}
																	</ListItemIcon>

																	<ListItemText
																		primaryTypographyProps={{
																			style: {
																				color:
																					location.pathname === item.url
																						? 'white'
																						: palette.kemek.darkestGray,
																				fontWeight: 400,
																				fontSize: 12,
																			},
																		}}
																		primary={item.title}
																	/>
																</ListItemButton>
															</Link>
														) : (
															<ListItemButton sx={{ pl: 2 }}>
																<ListItemIcon>
																	{item.icon && <LazySvg name={item.icon} />}
																</ListItemIcon>

																<ListItemText primary={item.title} />
															</ListItemButton>
														)}
													</ListItem>
												</List>
											</Collapse>
										))}
								</Box>
							);
						})}
				</List>

				{isAdmin && isSidebarOpen && (
					<Box sx={{ pt: 2 }}>
						<Button
							startIcon={<IconArrow />}
							component={Link}
							to="/results"
							variant="text"
							color="secondary"
							fullWidth
						>
							{t('admin.goBackToClient')}
						</Button>
					</Box>
				)}

				<Box sx={{ flex: 1 }}>{/* empty space */}</Box>

				<Box
					sx={{
						display: { xs: 'none', md: isSidebarOpen ? 'flex' : 'none' },
						alignItems: 'center',
						fontSize: 12,
						mt: '20px',
						color: (theme: Theme) => theme.palette.kemek.lightGray,
					}}
				>
					{appVersion || '-'}
				</Box>

				{/* {isSidebarOpen && <SidebarSpace />} */}
			</Box>
		</Drawer>
	);
};

export default MenuSidebar;
